const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
/** Stays below the ~4096 byte per-cookie browser limit, including name and attributes. */
const MAX_CHUNK_LENGTH = 3800;
const MAX_CHUNK_COUNT = 16;
const MAX_ENCODED_LENGTH = MAX_CHUNK_LENGTH * MAX_CHUNK_COUNT;

type CookieManifest = { readonly generation?: string; readonly count: number };

let writeSequence = 0;

function writeCookie(name: string, encodedValue: string, maxAgeSeconds: number = MAX_AGE_SECONDS): void {
    const secure = window.location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `${name}=${encodedValue};path=/;max-age=${maxAgeSeconds};SameSite=Lax${secure}`;
}

function readCookie(name: string): string | null {
    const prefix = `${name}=`;
    return (
        document.cookie
            .split(';')
            .map((cookie) => cookie.trimStart())
            .find((cookie) => cookie.startsWith(prefix))
            ?.slice(prefix.length) ?? null
    );
}

function parseManifest(value: string | null): CookieManifest | null {
    if (!value) {
        return null;
    }
    const versioned = /^v2:([a-z0-9]+):(\d+)$/.exec(value);
    if (versioned) {
        const count = Number(versioned[2]);
        return Number.isSafeInteger(count) && count > 0 && count <= MAX_CHUNK_COUNT
            ? { generation: versioned[1], count }
            : null;
    }
    const count = Number(value);
    return Number.isSafeInteger(count) && count > 0 && count <= MAX_CHUNK_COUNT ? { count } : null;
}

function chunkCookieName(name: string, manifest: CookieManifest, index: number): string {
    return manifest.generation ? `${name}.${manifest.generation}.${index}` : `${name}.${index}`;
}

function readManifestValue(name: string, manifest: CookieManifest): string | null {
    let encoded = '';
    for (let index = 0; index < manifest.count; index++) {
        const chunk = readCookie(chunkCookieName(name, manifest, index));
        if (chunk === null || encoded.length + chunk.length > MAX_ENCODED_LENGTH) {
            return null;
        }
        encoded += chunk;
    }
    return encoded;
}

function removeManifestChunks(name: string, value: string | null): void {
    const manifest = parseManifest(value);
    if (!manifest) {
        return;
    }
    for (let index = 0; index < manifest.count; index++) {
        removeCookie(chunkCookieName(name, manifest, index));
    }
}

export function removeCookie(name: string): void {
    writeCookie(name, '', 0);
}

/** Stages bounded JSON chunks, then switches the manifest so the prior value remains readable on failure. */
export function writeChunkedJsonCookie(name: string, value: unknown): boolean {
    let encoded: string;
    try {
        const serialized = JSON.stringify(value);
        if (serialized === undefined) {
            return false;
        }
        encoded = encodeURIComponent(serialized);
    } catch {
        return false;
    }
    if (encoded.length > MAX_ENCODED_LENGTH) {
        return false;
    }

    const previousValue = readCookie(name);
    const count = Math.ceil(encoded.length / MAX_CHUNK_LENGTH);
    const manifest: CookieManifest = {
        generation: `${Date.now().toString(36)}${(++writeSequence).toString(36)}`,
        count
    };
    const nextValue = `v2:${manifest.generation}:${count}`;

    try {
        for (let index = 0; index < count; index++) {
            const cookieName = chunkCookieName(name, manifest, index);
            const chunk = encoded.slice(index * MAX_CHUNK_LENGTH, (index + 1) * MAX_CHUNK_LENGTH);
            writeCookie(cookieName, chunk);
            if (readCookie(cookieName) !== chunk) {
                removeManifestChunks(name, nextValue);
                return false;
            }
        }

        writeCookie(name, nextValue);
        if (readCookie(name) !== nextValue) {
            removeManifestChunks(name, nextValue);
            return false;
        }
    } catch {
        removeManifestChunks(name, nextValue);
        return false;
    }

    removeManifestChunks(name, previousValue);
    return true;
}

export function readChunkedJsonCookie(name: string): unknown {
    const manifest = parseManifest(readCookie(name));
    if (!manifest) {
        return null;
    }
    const encoded = readManifestValue(name, manifest);
    if (encoded === null) {
        return null;
    }
    try {
        return JSON.parse(decodeURIComponent(encoded));
    } catch {
        return null;
    }
}
